import React, { useState, useEffect } from 'react';
import styled from 'styled-components';

const OverlayContainer = styled.div`
  position: absolute;
  bottom: 80px;
  left: 0;
  right: 0;
  display: flex;
  justify-content: center;
  pointer-events: none;
  z-index: 10;
`;

const SubtitleText = styled.div`
  font-family: ${props => props.fontFamily};
  font-size: ${props => props.fontSize}px;
  font-weight: ${props => props.fontWeight};
  color: ${props => props.textColor};
  background-color: ${props => props.backgroundColor};
  padding: 12px 20px;
  border-radius: 6px;
  text-align: center;
  line-height: 1.4;
  max-width: 80%;
  word-wrap: break-word;
  white-space: nowrap;
  animation: ${props => props.animate ? 'fadeInOut 0.5s ease-in-out' : 'none'};

  @keyframes fadeInOut {
    0% { opacity: 0; transform: translateY(10px); }
    20% { opacity: 1; transform: translateY(0); }
    80% { opacity: 1; transform: translateY(0); }
    100% { opacity: 0.8; transform: translateY(-2px); }
  }
`;

const KeywordHighlight = styled.span`
  background-color: ${props => props.highlightColor};
  color: ${props => props.highlightTextColor};
  padding: 2px 4px;
  border-radius: 2px;
  font-weight: 600;
`;

const SubtitleOverlay = ({ 
  project, 
  currentTime, 
  selectedLanguage = 'original',
  subtitleStyle 
}) => {
  const [currentWord, setCurrentWord] = useState('');
  const [wordAnimate, setWordAnimate] = useState(false);

  useEffect(() => {
    if (!project || !subtitleStyle?.enabled || !project.transcript) {
      setCurrentWord('');
      return;
    }

    // Get transcript data
    const transcript = project.transcript || [];
    const translations = project.translations || {};

    // Get segments based on selected language
    let segments = [];
    if (selectedLanguage === 'original') {
      segments = transcript;
    } else if (translations[selectedLanguage]) {
      segments = translations[selectedLanguage];
    } else {
      segments = transcript; // Fallback to original
    }

    // Find current segment based on time
    const currentSegment = segments.find(segment => 
      currentTime >= segment.startTime && currentTime <= segment.endTime
    );

    if (currentSegment) {
      console.log('📝 Current segment text:', currentSegment.text);
      console.log('🕐 Current time:', currentTime, 'Segment:', currentSegment.startTime, '-', currentSegment.endTime);
      
      // Split text into words
      const words = currentSegment.text.split(' ').filter(word => word.trim() !== '');
      const segmentDuration = currentSegment.endTime - currentSegment.startTime;
      const timePerWord = segmentDuration / words.length; // Time each word should be shown
      
      console.log('🔢 Total words:', words.length, 'Time per word:', timePerWord);
      
      // Calculate which word should be shown based on time within segment
      const timeInSegment = currentTime - currentSegment.startTime;
      const wordIndex = Math.floor(timeInSegment / timePerWord);
      const clampedIndex = Math.max(0, Math.min(wordIndex, words.length - 1));
      
      console.log('⏰ Time in segment:', timeInSegment, 'Word index:', wordIndex, 'Clamped:', clampedIndex);
      
      if (words[clampedIndex]) {
        const newWord = words[clampedIndex];
        console.log('✅ Showing word:', newWord);
        
        // Trigger animation if word changed
        if (newWord !== currentWord) {
          setWordAnimate(true);
          setCurrentWord(newWord);
          setTimeout(() => setWordAnimate(false), 500);
        }
      }
    } else {
      console.log('❌ No segment found for time:', currentTime);
      setCurrentWord('');
    }
  }, [currentTime, selectedLanguage, project, subtitleStyle]);

  // Don't render if subtitles are disabled or no word
  if (!subtitleStyle?.enabled || !currentWord) {
    return null;
  }

  const hexToRgba = (hex, opacity) => {
    if (!hex || !hex.startsWith('#')) return `rgba(0, 0, 0, ${opacity})`;
    const r = parseInt(hex.slice(1, 3), 16);
    const g = parseInt(hex.slice(3, 5), 16);
    const b = parseInt(hex.slice(5, 7), 16);
    return `rgba(${r}, ${g}, ${b}, ${opacity})`;
  };

  const renderWordWithHighlight = () => {
    if (!subtitleStyle.keywordHighlighting) {
      return currentWord;
    }

    // Simple keyword detection
    const shouldHighlight = currentWord.toLowerCase().includes('video') || 
                            currentWord.toLowerCase().includes('amazing') ||
                            currentWord.toLowerCase().includes('tutorial');
    
    return shouldHighlight ? (
      <KeywordHighlight
        highlightColor={subtitleStyle.highlightColor}
        highlightTextColor={subtitleStyle.highlightTextColor}
      >
        {currentWord}
      </KeywordHighlight>
    ) : (
      currentWord
    );
  };

  return (
    <OverlayContainer>
      <SubtitleText
        fontFamily={subtitleStyle.fontFamily || 'Arial'}
        fontSize={subtitleStyle.fontSize || 36}
        fontWeight={600}
        textColor={subtitleStyle.fontColor || '#ffffff'}
        backgroundColor={hexToRgba(subtitleStyle.backgroundColor || '#000000', (subtitleStyle.backgroundOpacity ?? 70) / 100)}
        animate={wordAnimate}
      >
        {renderWordWithHighlight()}
      </SubtitleText>
    </OverlayContainer>
  );
};

export default SubtitleOverlay; 